import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointAdminBusinessConfigDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import SearchInput from '../../../../../../components/base/input/search_input';
import SaveOrRollback from '../../../../../../components/app-specific/save-or-rollback';
import PageHeader from '../../components/page-header';
import {paginateMockItems} from '../../mock-search';
import {mockBizConfigs} from '../../mock-data';

const dataGridSaveConfig = {name: 'BizPointAdminBusinessConfigDataGrid', version: 2};
const col = BizPointAdminBusinessConfigDataGridColumnNames;

const editableFields = ['rateBizPer100', 'redeemPer900', 'freezeDays'];

const cloneConfigs = (items) => items.map((row) => ({...row}));

/**
 * Returns whether draft editable fields differ from the saved baseline.
 */
const isConfigRowDirty = (draft, saved) => {
    if (!draft || !saved) {
        return false;
    }
    return editableFields.some((field) => draft[field] !== saved[field]);
};

/**
 * Applies a row patch to both the full draft list and the current page slice.
 */
const patchBusinessRow = (items, business, updater) =>
    items.map((row) => (row.business === business ? updater(row) : row));

const searchOptions = {
    keywordFields: ['business', 'currency', 'tier'],
    sortFieldMap: {
        [col.business]: 'business',
        [col.currency]: 'currency',
        [col.tier]: 'tier',
    },
};

const AdminBusinessConfigView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.businessConfig'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportAdminBusinessConfig = operatorAccessLevels.packages.bizPoint.exportAdminBusinessConfig;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-admin-business-config');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    /** Committed baseline — updated only after a successful per-row save. */
    const [savedItems, setSavedItems] = useState(() => cloneConfigs(mockBizConfigs));
    /** Working copy — retains unsaved edits across partial save failures. */
    const [draftItems, setDraftItems] = useState(() => cloneConfigs(mockBizConfigs));
    const draftItemsRef = useRef(draftItems);
    draftItemsRef.current = draftItems;
    const [data, setData] = useState([]);
    const [savingBusiness, setSavingBusiness] = useState(null);

    /**
     * Configures notebook tags and behavioral analytics for business config.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointAdminBusinessConfig);
        configureBehavioralTool((tags) => tags.bizPointAdminBusinessConfig);
    }, [configureBehavioralTool, configureNotebookTags]);

    const savedByBusiness = useMemo(() => {
        const map = new Map();
        for (const row of savedItems) {
            map.set(row.business, row);
        }
        return map;
    }, [savedItems]);

    const columns = useMemo(() => [
        {title: t('business'), name: col.business, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('currency'), name: col.currency, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('rateBizPer100'), name: col.rateBizPer100, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.left, sortable: false, width: 140},
        {title: t('redeemPer900'), name: col.redeemPer900, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.left, sortable: false, width: 140},
        {title: t('freezeDays'), name: col.freezeDays, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.left, sortable: false, width: 120},
        {title: t('tier'), name: col.tier, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {
            title: '',
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.center,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 110,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Updates a draft field locally without re-running search / showing a loading flash.
     */
    const handleFieldChange = useCallback((business, field, value) => {
        const updater = (row) => ({...row, [field]: value});
        setDraftItems((prev) => patchBusinessRow(prev, business, updater));
        setData((prev) => patchBusinessRow(prev, business, updater));
    }, []);

    /**
     * Restores one row's draft from the last successful save.
     */
    const handleRollbackRow = useCallback((business) => {
        const saved = savedByBusiness.get(business);
        if (!saved) {
            return;
        }
        const updater = () => ({...saved});
        setDraftItems((prev) => patchBusinessRow(prev, business, updater));
        setData((prev) => patchBusinessRow(prev, business, updater));
    }, [savedByBusiness]);

    /**
     * Persists one dirty row. Other rows' drafts are left intact on failure.
     */
    const handleSaveRow = useCallback(async (business) => {
        const draft = draftItemsRef.current.find((row) => row.business === business);
        if (!draft) {
            return;
        }
        setSavingBusiness(business);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true, data: {...draft}});
        if (!isMounted()) {
            return;
        }
        setSavingBusiness(null);
        if (response?.resultFlag) {
            const committed = response.data ?? draft;
            const updater = () => ({...committed});
            setSavedItems((prev) => patchBusinessRow(prev, business, updater));
            setDraftItems((prev) => patchBusinessRow(prev, business, updater));
            setData((prev) => patchBusinessRow(prev, business, updater));
        }
    }, [isMounted]);

    /**
     * Loads the current page from the draft ref so edits do not recreate this callback.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(draftItemsRef.current, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(draftItems, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.rateBizPer100]: (item) => String(item.rateBizPer100),
            [col.redeemPer900]: (item) => String(item.redeemPer900),
            [col.freezeDays]: (item) => String(item.freezeDays),
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, draftItems, searchData.filters, searchData.orderBy, t]);

    const rows = useMemo(() => data.map((row) => {
        const saved = savedByBusiness.get(row.business);
        const dirty = isConfigRowDirty(row, saved);
        const saving = savingBusiness === row.business;
        return {
            key: row.business,
            data: row,
            cells: {
                [col.business]: row.business,
                [col.currency]: row.currency,
                [col.rateBizPer100]:
                    <TextField
                        size="small"
                        type="number"
                        variant="outlined"
                        value={row.rateBizPer100}
                        disabled={saving}
                        onChange={(e) => handleFieldChange(row.business, 'rateBizPer100', Number(e.target.value))}
                    />,
                [col.redeemPer900]:
                    <TextField
                        size="small"
                        type="number"
                        variant="outlined"
                        value={row.redeemPer900}
                        disabled={saving}
                        onChange={(e) => handleFieldChange(row.business, 'redeemPer900', Number(e.target.value))}
                    />,
                [col.freezeDays]:
                    <TextField
                        size="small"
                        type="number"
                        variant="outlined"
                        value={row.freezeDays}
                        disabled={saving}
                        onChange={(e) => handleFieldChange(row.business, 'freezeDays', Number(e.target.value))}
                    />,
                [col.tier]: row.tier,
                [col.actions]:
                    <SaveOrRollback
                        column={false}
                        showText={false}
                        show={dirty}
                        loading={saving}
                        onSave={() => handleSaveRow(row.business)}
                        onRollback={() => handleRollbackRow(row.business)}
                    />,
            },
        };
    }), [data, handleFieldChange, handleRollbackRow, handleSaveRow, savedByBusiness, savingBusiness]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            <Alert
                severity="info"
                variant="outlined"
                sx={{
                    mb: 2,
                    borderWidth: 1.5,
                    borderColor: 'info.main',
                    bgcolor: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(2, 136, 209, 0.12)'
                        : 'rgba(2, 136, 209, 0.06)',
                }}
            >
                {t('noCrossCurrencyNote')}
            </Alert>

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportAdminBusinessConfig ? getExportInfo : undefined}
            />
        </Box>
    );
};

export default AdminBusinessConfigView;
